import React from 'react';
import ReactMapGL, { NavigationControl, Marker } from 'react-map-gl';

import Cluster from './Cluster';
import styles from './styles.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const mapboxApiKey =
    'pk.eyJ1IjoiYmlncml2YWwiLCJhIjoiY2phMzRsanUzMHV6ajJxbGZzdDZsamtqayJ9.PngeT-c1grgQoIMXMhkBUA';

class Map extends React.Component {
    constructor(props) {
        super(props);
        this.cluster = React.createRef();
        this.handlePanned = this.handlePanned.bind(this);
        this.handleZoomed = this.handleZoomed.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);
        this.handleMarkerClick = this.handleMarkerClick.bind(this);
        this.handleCurrent = this.handleCurrent.bind(this);
        this.state = {
            dragEnd: null,
            zoomEnd: null,
            loaded: false,
            results: null,
            focusedMarker: null,
        };
    }

    componentDidMount() {
        this.setState({
            loaded: true,
        });
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!prevProps.current && this.props.current) {
            this.handleCurrent();
        }
    }

    handlePanned() {
        const bounds = this.props?.$map?.current?.getMap()?.getBounds();

        if (this.props.onPanned) {
            this.props.onPanned(bounds);
        }

        if (this.cluster.current && this.cluster.current.onPanned) {
            this.cluster.current.onPanned();
        }
    }

    handleZoomed() {
        const bounds = this.props.$map.current.getMap().getBounds();

        if (this.props.onZoomed) {
            this.props.onZoomed(bounds);
        }

        if (this.cluster.current && this.cluster.current.onZoomed) {
            this.cluster.current.onZoomed();
        }
    }

    handleViewportChange(viewport, interactionState) {
        clearTimeout(this.state.dragEnd);
        clearTimeout(this.state.zoomEnd);

        if (this.props.onViewportChange) {
            this.props.onViewportChange({
                longitude: viewport.longitude || this.props.longitude,
                latitude: viewport.latitude || this.props.latitude,
                zoom: viewport.zoom < 4 ? 4 : viewport.zoom || this.props.zoom,
            });
        }

        this.setState({
            dragEnd:
                interactionState && interactionState.isPanning
                    ? setTimeout(() => this.handlePanned(), 300)
                    : null,
            zoomEnd:
                interactionState && interactionState.isZooming
                    ? setTimeout(() => this.handleZoomed(), 300)
                    : null,
        });
    }

    handleMarkerClick(marker) {
        this.setState({
            focusedMarker: marker,
        });
    }

    handleCurrent(zoom) {
        // otherwise on mixed or list view zoom in to the cluster by increments of 2
        this.handleViewportChange(
            {
                longitude: this.props.current[0],
                latitude: this.props.current[1],
                zoom: zoom ? this.props.zoom + 2 : this.props.zoom,
            },
            { isZooming: true }
        );
    }

    render() {
        const ClusterMarker = ({ cluster, markers }) => {
            const handleCluster = () => {
                // otherwise on mixed or list view zoom in to the cluster by increments of 2
                this.handleViewportChange(
                    {
                        longitude: cluster.geometry.coordinates[0],
                        latitude: cluster.geometry.coordinates[1],
                        zoom: this.props.zoom + 2,
                    },
                    { isZooming: true }
                );
            };

            return (
                <span className={styles.markerCluster} onClick={handleCluster}>
                    <span>{cluster.properties.point_count}</span>
                </span>
            );
        };

        const markers = this.props.markers.map((marker, index) => {
            return (
                <Marker
                    key={marker.key || index}
                    longitude={marker.coordinates[0]}
                    latitude={marker.coordinates[1]}
                    offsetLeft={-19}
                    offsetTop={-8}
                >
                    <div
                        className={styles.marker}
                        onClick={
                            this.props.onMarkerFocus
                                ? e => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      this.props.onMarkerFocus(marker);
                                  }
                                : undefined
                        }
                    >
                        {this.props.renderMarker ? this.props.renderMarker(marker) : marker.text}
                    </div>
                </Marker>
            );
        });

        return (
            <div
                className={styles.map}
                style={{ height: this.props.height }}
                onClick={this.props.onMarkerBlur ? this.props.onMarkerBlur : undefined}
            >
                <div className={styles.mapGl}>
                    <ReactMapGL
                        mapStyle="mapbox://styles/mapbox/light-v10?optimize=true"
                        width={'100%'}
                        height={'100%'}
                        latitude={this.props.latitude}
                        longitude={this.props.longitude}
                        zoom={this.props.zoom > 20 ? 20 : this.props.zoom}
                        maxZoom={20}
                        minZoom={4}
                        mapboxApiAccessToken={mapboxApiKey}
                        ref={this.props.$map}
                        onViewportChange={this.props.$map.current && this.handleViewportChange}
                    >
                        <NavigationControl
                            onViewportChange={viewport =>
                                this.props.$map.current &&
                                this.handleViewportChange(viewport, { isZooming: true })
                            }
                            className={styles.mapZoom}
                            showCompass={false}
                        />

                        {this.props.current && (
                            <Marker
                                key="current_marker"
                                longitude={this.props.current[0]}
                                latitude={this.props.current[1]}
                                offsetLeft={-12}
                                offsetTop={-32}
                            >
                                <FontAwesomeIcon
                                    onClick={() => this.handleCurrent(true)}
                                    icon={'map-marker-alt'}
                                    className={styles.markerCurrent}
                                />
                            </Marker>
                        )}

                        {this.props.markers?.length ? (
                            <Cluster
                                ref={this.cluster}
                                map={this.props.$map}
                                radius={10}
                                extent={512}
                                nodesize={40}
                                element={ClusterMarker}
                            >
                                {markers}
                            </Cluster>
                        ) : null}
                    </ReactMapGL>
                </div>
            </div>
        );
    }
}

Map.defaultProps = {
    height: 600,
};

export default Map;
